import "./MessageWithDbComponent.scss";
import { ChatMessage } from "@/models/chat.model.ts";
import { useSelector } from "react-redux";
import { IRootReducerState } from "@/store/reducers";
import { useCallback, useEffect, useState, useMemo } from "react";
import commonService from "@/services/common.service.ts";
import { Button } from "@/components/ui/button.tsx";
import { ImageConfig } from "@/constants";
import FileListStreaming from "@/components/common-components/file-list-streaming/FileListStreaming";
import ChatLoaderComponent from "@/components/common-components/chat-loader/ChatLoaderComponent.tsx";
import AssetPreviewDialog from "@/components/common-components/asset-preview-dialog/AssetPreviewDialog";
import MarkdownTextRenderer from "@/components/common-components/markdown-text-renderer/MarkdownTextRenderer";
import ScratchPadComponent from "@/components/common-components/scratch-pad/ScratchPadComponent";
import AdvancedModeProcessLoaderComponent, { ProcessItem } from "@/components/common-components/advanced-mode-process-loader/AdvancedModeProcessLoaderComponent";
import { CommonService } from "@/services";
import { File, Image, FileSpreadsheet, ChevronDown, ChevronRight, Database, AlertCircle, Clock, Gift } from "lucide-react";
import React from "react";
import moment from "moment";
import {
    Dialog,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogContentNoClose,
} from "@/components/ui/dialog";

interface MessageWithDbComponentProps {
    message: ChatMessage;
    isLast: boolean;
    projectId: string;
    type?: "chat-step" | "on-boarding-step" | "pm-step" | "requirement-step"
}

interface ParsedAsset {
    url: string;
    name: string;
    type: string;
}

interface ParsedErrorDescription {
    id: string;
    content: string;
    isExpanded: boolean;
}

interface ParsedNetworkError {
    id: string;
    content: string;
    isExpanded: boolean;
}

const MessageWithDbComponent = (props: MessageWithDbComponentProps) => {
    const { message, isLast, projectId } = props;

    const currentUser = useSelector((state: IRootReducerState) => state.auth.user);
    const currentProjectDetails = useSelector((state: IRootReducerState) =>
        state.projectData.currentProjectDetails);
    const subscriptions = useSelector((state: IRootReducerState) => state.meta.subscriptions);

    // Determine if user is on free plan
    const userSubscription = currentUser?.subscription;
    const currentPlanId = userSubscription?.plan_id;
    const currentPlanDetails = Array.isArray(subscriptions)
        ? (subscriptions as any[]).find((sub: any) => sub.id === currentPlanId)
        : null;
    const currentPlanName = currentPlanDetails?.plan_name || userSubscription?.plan_name || "Free";
    const isFreeUser = !currentPlanId || currentPlanName.toLowerCase() === "free" || !userSubscription;

    const [isApplying, setIsApplying] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [localApplied, setLocalApplied] = useState<boolean>(message?.extra?.db_query_result || false);
    const [hasStreamEnded, setHasStreamEnded] = useState(false);
    const [assets, setAssets] = useState<ParsedAsset[]>([]);
    const [errorDescriptions, setErrorDescriptions] = useState<ParsedErrorDescription[]>([]);
    const [networkErrors, setNetworkErrors] = useState<ParsedNetworkError[]>([]);
    const [cleanMessage, setCleanMessage] = useState<string>("");
    const [previewAsset, setPreviewAsset] = useState<{ src?: string; file?: any; alt?: string } | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const hasMigration = !!message?.extra?.migration_id;
    const shouldShowControls = (hasMigration && isLast && !localApplied && (hasStreamEnded || (message?.extra?.migration_details && message?.extra?.migration_details?.status !== "success"))) || (message?.extra?.isAdvancedMode && hasMigration && !localApplied);
    const migrationDetails = message?.extra?.migration_id ? `Migration ID: ${message?.extra?.migration_id}` : '';

    // State for database changes dialog
    const [showDbChangesDialog, setShowDbChangesDialog] = useState(false);

    // Show dialog when controls should be shown (blocking user flow until applied)
    useEffect(() => {
        if (shouldShowControls && !localApplied) {
            setShowDbChangesDialog(true);
        } else {
            setShowDbChangesDialog(false);
        }
    }, [shouldShowControls, localApplied]);

    // --- normalize scratchPad fields ---
    const scratchPadText =
        message?.extra?.scratchPad || message?.extra?.scratch_pad || null;
    const scratchPadActive = false;
    const scratchPadDone = false;

    // Parse assets and error descriptions from message content
    useEffect(() => {
        let processedMessage = message?.message || '';
        let allAssets: ParsedAsset[] = [];

        // Parse assets from message content
        if (processedMessage) {
            const assetRegex = /<assets>\[(.*?)\]<\/assets>/;
            const assetMatch = processedMessage.match(assetRegex);

            if (assetMatch) {
                const assetUrls = assetMatch[1].split(',').map(url => url.trim()).filter(url => url);
                const parsedAssets: ParsedAsset[] = assetUrls.map(url => {
                    const urlParts = url.split('/');
                    const fileName = urlParts[urlParts.length - 1] || 'Unknown file';
                    const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';

                    let type = 'file';
                    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension)) {
                        type = 'image';
                    } else if (['csv', 'xlsx', 'xls'].includes(fileExtension)) {
                        type = 'spreadsheet';
                    }

                    return {
                        url,
                        name: decodeURIComponent(fileName),
                        type
                    };
                });

                allAssets = [...allAssets, ...parsedAssets];
                // Remove assets tag from message content
                processedMessage = processedMessage.replace(assetRegex, '').trim();
            }
        }

        // Add imagePreviewUrl as an asset if it exists
        if (message?.extra?.imagePreviewUrl) {
            const imageUrl = message.extra.imagePreviewUrl;
            const urlParts = imageUrl.split('/');
            const fileName = urlParts[urlParts.length - 1] || 'uploaded-image';

            allAssets.push({
                url: imageUrl,
                name: decodeURIComponent(fileName),
                type: 'image'
            });
        }

        setAssets(allAssets);

        // Parse error descriptions
        if (processedMessage) {
            const errorDescRegex = /<error_description>(.*?)<\/error_description>/gs;
            const errorDescMatches = [...processedMessage.matchAll(errorDescRegex)];

            if (errorDescMatches.length > 0) {
                const parsedErrorDescriptions: ParsedErrorDescription[] = errorDescMatches.map((match, index) => ({
                    id: `error-desc-${index}`,
                    content: match[1].trim(),
                    isExpanded: false
                }));

                setErrorDescriptions(parsedErrorDescriptions);
                // Remove error_description tags from message content
                processedMessage = processedMessage.replace(errorDescRegex, '').trim();
            } else {
                setErrorDescriptions([]);
            }
        } else {
            setErrorDescriptions([]);
        }

        // Parse network errors
        if (processedMessage) {
            const networkErrorRegex = /<network_errors>(.*?)<\/network_errors>/gs;
            const networkErrorMatches = [...processedMessage.matchAll(networkErrorRegex)];

            if (networkErrorMatches.length > 0) {
                const parsedNetworkErrors: ParsedNetworkError[] = networkErrorMatches.map((match, index) => ({
                    id: `network-error-${index}`,
                    content: match[1].trim(),
                    isExpanded: false
                }));

                setNetworkErrors(parsedNetworkErrors);
                // Remove network_errors tags from message content
                processedMessage = processedMessage.replace(networkErrorRegex, '').trim();
            } else {
                setNetworkErrors([]);
            }
        } else {
            setNetworkErrors([]);
        }

        setCleanMessage(processedMessage);
    }, [message]);

    // Toggle error description expansion
    const toggleErrorDescription = useCallback((errorId: string) => {
        commonService.handleMixpanelTrack("Error description toggled", {
            error_id: errorId,
            project_id: projectId,
        });
        setErrorDescriptions(prev =>
            prev.map(error =>
                error.id === errorId
                    ? { ...error, isExpanded: !error.isExpanded }
                    : error
            )
        );
    }, [projectId]);

    // Toggle network error expansion
    const toggleNetworkError = useCallback((errorId: string) => {
        commonService.handleMixpanelTrack("Network error toggled", {
            error_id: errorId,
            project_id: projectId,
        });
        setNetworkErrors(prev =>
            prev.map(error =>
                error.id === errorId
                    ? { ...error, isExpanded: !error.isExpanded }
                    : error
            )
        );
    }, [projectId]);

    // Handle asset preview
    const handleAssetPreview = useCallback((asset: ParsedAsset) => {
        setPreviewAsset({
            src: asset.url,
            file: { name: asset.name, content_type: asset.type === 'image' ? 'image/png' : 'application/octet-stream' },
            alt: asset.name
        });
        setIsPreviewOpen(true);
    }, []);

    const handleClosePreview = useCallback(() => {
        setIsPreviewOpen(false);
        setPreviewAsset(null);
    }, []);

    // Handle applying database changes
    const applyDatabaseChanges = useCallback((message: ChatMessage) => {
        setIsApplying(true);
        setError(null);
        const payload = {};

        commonService._project_service.applyDataBaseChanges(message?.extra?.migration_id, payload)
            .then(() => {
                setLocalApplied(true);
                commonService._alert.showToast("Database changes applied successfully", "success");
            })
            .catch((error: any) => {
                console.error("Error applying database changes:", error);
                setError(error?.error || "Failed to apply database changes");
                commonService._alert.showToast("Error applying changes", "error");
            })
            .finally(() => {
                setIsApplying(false);
            });
    }, []);


    // Check if user can connect to Supabase
    const hasSupabaseProject = Boolean(currentProjectDetails?.supabase_creds?.supabase_project_id);
    const supbaseConnected = hasSupabaseProject;

    // Handle Supabase connection
    const connectToSupabase = useCallback(() => {
        commonService._communications.connectSupabaseSubject.next({
            supabase_cred_id: currentUser?.supabase_cred_id
        });
    }, [currentUser]);

    const handleDbExecuteError = useCallback((error: any) => {
        const errorMessage = "Fix the error i got while executing the query in Supabase \n Error:" + error;
        // Close the dialog before sending the message
        setShowDbChangesDialog(false);
        setLocalApplied(true); // Mark as applied to prevent dialog from reopening
        commonService._communications.sendMessageSubject.next({
            message: errorMessage
        });
        commonService.handleMixpanelTrack("fix_database_error_clicked", {
            project_id: projectId,
            migration_id: message?.extra?.migration_id
        });
    }, [projectId, message?.extra?.migration_id]);

    // Handle continuing with static data (skip database changes)
    const handleContinueWithStaticData = useCallback(() => {
        setShowDbChangesDialog(false);
        setLocalApplied(true); // Mark as applied to hide the dialog
        const staticDataMessage = "Continue with static/mock data for now. Skip the database changes and use hardcoded sample data instead so I can preview the app functionality. And remove all the code related to supabase.";
        commonService._communications.sendMessageSubject.next({
            message: staticDataMessage
        });

    }, []);

    useEffect(() => {
        const endSub = CommonService._communications.endCodeStreamerSubject.subscribe((value: boolean) => {
            if (value) {
                setHasStreamEnded(true);
            }
        });
        return () => {
            endSub.unsubscribe();
        }
    }, []);

    const getAssetIcon = (type: string) => {
        switch (type) {
            case 'image':
                return <Image className="w-4 h-4 text-blue-500" />;
            case 'spreadsheet':
                return <FileSpreadsheet className="w-4 h-4 text-green-500" />;
            default:
                return <File className="w-4 h-4 text-gray-500" />;
        }
    };

    const renderAssetPreview = (asset: ParsedAsset) => {
        if (asset.type === 'image') {
            return (
                <img
                    src={asset.url}
                    alt={asset.name}
                    className="w-full h-12 object-cover rounded-t-lg"
                />
            );
        }
        return (
            <div className="w-full h-20 bg-gray-50 dark:bg-neutral-800 rounded-t-lg flex items-center justify-center">
                {getAssetIcon(asset.type)}
            </div>
        );
    };

    // Render error description as collapsible section
    const renderErrorDescription = (errorDesc: ParsedErrorDescription) => (
        <div key={errorDesc.id} className="error-description-container mb-3">
            <button
                onClick={() => toggleErrorDescription(errorDesc.id)}
                className={`error-description-header flex items-center gap-2 p-2 border border-gray-600 hover:bg-gray-800/20 transition-colors w-full text-left ${errorDesc.isExpanded ? 'rounded-t-lg' : 'rounded-lg'
                    }`}
                style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
            >
                {errorDesc.isExpanded ? (
                    <ChevronDown className="w-4 h-4" style={{ color: 'var(--text-muted-ash)' }} />
                ) : (
                    <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-muted-ash)' }} />
                )}
                <span className="text-sm font-medium" style={{ color: 'var(--text-muted-ash)' }}>Error Details</span>
            </button>
            {errorDesc.isExpanded && (
                <div className="error-description-content p-3 border-x border-b border-gray-600 rounded-b-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                    <pre className="text-xs whitespace-pre-wrap break-words font-mono" style={{ color: 'var(--text-muted-ash)' }}>
                        {errorDesc.content}
                    </pre>
                </div>
            )}
        </div>
    );

    // Render network error as collapsible section
    const renderNetworkError = (networkError: ParsedNetworkError) => (
        <div key={networkError.id} className="network-error-container mb-3">
            <button
                onClick={() => toggleNetworkError(networkError.id)}
                className={`network-error-header flex items-center gap-2 p-2 border hover:bg-orange-800/20 transition-colors w-full text-left ${networkError.isExpanded ? 'rounded-t-lg' : 'rounded-lg'
                    }`}
                style={{ backgroundColor: 'rgba(255,165,0,0.05)' }}
            >
                {networkError.isExpanded ? (
                    <ChevronDown className="w-4 h-4" style={{ color: 'var(--text-muted-ash)' }} />
                ) : (
                    <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-muted-ash)' }} />
                )}
                <span className="text-sm font-medium" style={{ color: 'var(--text-muted-ash)' }}>Network Error Details</span>
            </button>
            {networkError.isExpanded && (
                <div className="network-error-content p-3 border-x border-b rounded-b-lg" style={{ backgroundColor: 'rgba(255,165,0,0.05)' }}>
                    <pre className="text-xs whitespace-pre-wrap break-words font-mono" style={{ color: 'var(--text-muted-ash)' }}>
                        {networkError.content}
                    </pre>
                </div>
            )}
        </div>
    );

    const handleRetryChatMessage = useCallback(() => {
        commonService._communications.retryChatMessageSubject.next({ retryMessage: true });
    }, []);

    const handleUpgradeOrAddTokens = useCallback(() => {
        commonService._communications.showUpgradeOrTopupDialogSubject.next({
            message: "You've run out of credits. " + (isFreeUser ? "Upgrade to a paid plan or add more credits to continue building." : "Add more tokens to continue building.")
        });
    }, [isFreeUser]);

    // Check if error is insufficient budget
    const isInsufficientBudgetError = message?.type === "error" &&
        message?.message?.toLowerCase().includes("insufficient budget");

    // Calculate next credit refresh time (UTC 00:00) in user's local timezone
    const nextCreditRefreshTime = useMemo(() => {
        const nextMidnightUTC = moment.utc().endOf('day').add(1, 'second');
        return nextMidnightUTC.local().format('h:mm A');
    }, []);

    if (message?.type === "error") {
        return (
            <div className={`message-container ${message.role === 'agent' ? 'ai-message' : ''}`}>
                <div className="message-content">
                    <div className="text-red-500 text-sm mb-3">
                        {message.message}
                    </div>
                    {isInsufficientBudgetError ? (
                        <>
                            <div className="insufficient-budget-actions">
                                {isFreeUser && (
                                    <div className="free-credits-card">
                                        <div className="free-credits-card__icon">
                                            <Gift className="w-5 h-5" />
                                        </div>
                                        <div className="free-credits-card__content">
                                            <p className="free-credits-card__title">Free credits refresh daily!</p>
                                            <p className="free-credits-card__description">
                                                <Clock className="w-3.5 h-3.5 inline-block mr-1" />
                                                Come back at <span className="free-credits-card__time">{nextCreditRefreshTime}</span> for more free credits
                                            </p>
                                        </div>
                                    </div>
                                )}



                            </div>
                            <Button
                                onClick={handleUpgradeOrAddTokens}
                                mixpanelEvent={{
                                    name: isFreeUser ? "upgrade_button_clicked_from_insufficient_budget_error" : "add_tokens_button_clicked_from_insufficient_budget_error",
                                    properties: { project_id: projectId, is_free_user: isFreeUser }
                                }}
                                className="mt-3"
                            >
                                {isFreeUser ? "Add Credits / Upgrade" : "Add Tokens"}
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={handleRetryChatMessage}
                            className="retry-btn bg-red-500 text-white hover:bg-red-600 h-8 px-3 text-sm"
                            loading={isApplying}
                            loadingText={'Retrying...'}
                            mixpanelEvent={{ name: "retry_chat_message_button_clicked_from_chat_message", properties: { project_id: projectId } }}
                        >
                            Try Again
                        </Button>
                    )}
                </div>
            </div>
        )
    }
    // Check if this is an advanced mode message with process items
    const processItems = message?.extra?.processItems as ProcessItem[] | undefined;

    return (
        <div className={`message-container ${message.role === 'agent' ? 'ai-message' : ''}`}>
            {!message?.message && !(Object.keys(message?.extra || {}).length > 0) && (
                <ChatLoaderComponent type={"chat-step"} className="mt-3 ml-4" />
            )}
            {
                processItems && processItems?.length > 0 && (
                    <AdvancedModeProcessLoaderComponent
                        items={processItems}
                    />
                )
            }

            {/* Display Assets */}
            {assets?.length > 0 && (
                <div className="message-assets mb-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3">
                        {assets.map((asset, index) => (
                            <div
                                key={index}
                                className="bg-white dark:bg-neutral-800 rounded-md shadow-sm border border-gray-200 dark:border-neutral-700 overflow-hidden hover:shadow-lg hover:scale-105 transition-all duration-200 cursor-pointer group"
                                onClick={() => handleAssetPreview(asset)}
                                title={`Click to preview ${asset.name}`}
                            >
                                {/* Asset Preview */}
                                <div className="relative group-hover:opacity-90 transition-opacity">
                                    {renderAssetPreview(asset)}
                                </div>

                            </div>
                        ))}
                    </div>
                </div>
            )}
            {/* Message Content */}
            <div className="message-content">
                <ScratchPadComponent
                    scratchPadText={scratchPadText}
                    scratchPadActive={scratchPadActive}
                    scratchPadDone={scratchPadDone}
                />
                <MarkdownTextRenderer text={cleanMessage} />
            </div>

            {/* Error Descriptions */}
            {errorDescriptions?.length > 0 && (
                <div className="error-descriptions mt-3">
                    {errorDescriptions.map(renderErrorDescription)}
                </div>
            )}

            {/* Network Errors */}
            {networkErrors?.length > 0 && (
                <div className="network-errors mt-3">
                    {networkErrors.map(renderNetworkError)}
                </div>
            )}

            {/* File List Streaming / Action Card */}
            {(message.role === 'agent' || (Object.keys(message?.extra?.files || {})?.length > 0) || (processItems && processItems.length > 0)) && (
                <FileListStreaming
                    message={message}
                    projectId={projectId}
                    appType={currentProjectDetails?.app_type}
                />
            )}

            {/* Migration Controls */}
            {shouldShowControls && (
                <div className="migration-controls mt-4">
                    <div className="mb-2">
                        <span className="font-medium">Database Changes Detected</span>
                        {migrationDetails && (
                            <span className="text-sm text-gray-500 ml-2">({migrationDetails})</span>
                        )}
                    </div>

                    {!supbaseConnected ? (
                        <div className="supabase-connection">
                            <div className="text-sm text-amber-600 mb-2">
                                Please connect your Supabase project to apply database changes.
                            </div>
                            <Button
                                className="p-0 pt-1"
                                onClick={connectToSupabase}
                                variant="icon"
                                mixpanelEvent={{ name: "connect_supabase_button_clicked_from_chat_message", properties: { project_id: projectId } }}
                            >
                                <ImageConfig.SupabaseConnectIconSVG className="size-full h-full" />
                            </Button>
                        </div>
                    ) : error ? (
                        <div className="error-state">
                            <div className="text-sm text-red-500 mb-2">
                                Error: {error}
                            </div>
                            <Button
                                onClick={handleDbExecuteError.bind(this, error)}
                                className="retry-btn bg-red-500 text-white hover:bg-red-600 h-8 px-3 text-sm"
                                loading={isApplying}
                                loadingText={'Retrying...'}
                                mixpanelEvent={{ name: "retry_database_query_button_clicked_from_chat_message", properties: { project_id: projectId } }}
                            >
                                Fix the Database query
                            </Button>
                        </div>
                    ) : (
                        <Button
                            onClick={applyDatabaseChanges.bind(this, message)}
                            disabled={isApplying}
                            className="apply-changes-btn bg-blue-500 text-white hover:bg-blue-600 h-9 px-4 text-sm"
                            loading={isApplying}
                            loadingText={'Applying Changes...'}
                            mixpanelEvent={{ name: "apply_database_changes_button_clicked_from_chat_message", properties: { project_id: projectId } }}
                        >
                            Apply Database Changes
                        </Button>
                    )}
                </div>
            )}

            {/* Success Message */}
            {hasMigration && (message.is_migrate_success || localApplied || message?.extra?.migration_details?.status === "success") && (
                <div className="applied-success mt-4">
                    <span className="text-green-600 text-sm">✓ Database changes successfully applied</span>
                </div>
            )}

            {/* Asset Preview Dialog */}
            <AssetPreviewDialog
                isOpen={isPreviewOpen}
                onClose={handleClosePreview}
                asset={previewAsset}
            />

            {/* Database Changes Required Dialog */}
            <Dialog open={showDbChangesDialog} onOpenChange={() => { }}>
                <DialogContentNoClose className="database-changes-dialog">
                    <DialogHeader>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-3 rounded-full bg-blue-500/10">
                                <Database className="w-6 h-6 text-blue-500" />
                            </div>
                            <DialogTitle className="text-xl">Database Changes Required</DialogTitle>
                        </div>
                        <DialogDescription className="text-base leading-relaxed pt-2">
                            Your application has pending database changes that need to be applied.
                            {migrationDetails && (
                                <span className="block text-sm text-muted-foreground mt-2">
                                    {migrationDetails}
                                </span>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="my-4 p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                        <div className="flex gap-2 mb-2">
                            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-sm mb-1">Why is this needed?</h4>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    Database migrations ensure your app's data structure stays in sync with your code.
                                    These changes need to be applied to your Supabase database for your app to function correctly.
                                </p>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        {!supbaseConnected ? (
                            <div className="w-full flex flex-col gap-3">
                                <div className="text-sm text-amber-600 text-center">
                                    Please connect your Supabase project to apply database changes.
                                </div>
                                <div className="flex gap-3 w-full">
                                    <Button
                                        onClick={handleContinueWithStaticData}
                                        variant="outline"
                                        className="flex-1"
                                        size="lg"
                                        mixpanelEvent={{ name: "continue_with_static_data_button_clicked_from_db_dialog", properties: { project_id: projectId } }}
                                    >
                                        Use Static Data
                                    </Button>
                                    <Button
                                        className="flex-1"
                                        onClick={connectToSupabase}
                                        size="lg"
                                        mixpanelEvent={{ name: "connect_supabase_button_clicked_from_db_dialog", properties: { project_id: projectId } }}
                                    >
                                        Connect Supabase
                                    </Button>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="w-full flex flex-col gap-3">
                                <div className="text-sm text-red-500 text-center">
                                    Error: {error}
                                </div>
                                <Button
                                    onClick={handleDbExecuteError.bind(this, error)}
                                    className="w-full bg-red-500 text-white hover:bg-red-600"
                                    size="lg"
                                    loading={isApplying}
                                    loadingText={'Fixing...'}
                                    mixpanelEvent={{ name: "fix_database_query_button_clicked_from_db_dialog", properties: { project_id: projectId } }}
                                >
                                    Fix the Database Query
                                </Button>
                            </div>
                        ) : (
                            <Button
                                onClick={applyDatabaseChanges.bind(this, message)}
                                disabled={isApplying}
                                className="w-full"
                                size="lg"
                                loading={isApplying}
                                loadingText={'Applying Changes...'}
                                mixpanelEvent={{ name: "apply_database_changes_button_clicked_from_db_dialog", properties: { project_id: projectId } }}
                            >
                                <Database className="w-4 h-4 mr-2" />
                                Apply Database Changes
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContentNoClose>
            </Dialog>
        </div>
    );
};

export default React.memo(MessageWithDbComponent);
